import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router'
import { useTranslation } from 'react-i18next'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import { ThemeToggle } from '@/components/ThemeToggle'
import { usePostApiUsersUserIdConfirmationToken } from '@/api/generated/public/public'
import { isServerUnreachable } from '@/lib/apiError'
import { Button } from '@/components/shadcn/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card'
import { WallpaperBackground } from '@/pages/login/components/WallpaperBackground'

type ConfirmationState = 'pending' | 'success' | 'error'

export default function EmailConfirmationPage() {
  const { t } = useTranslation('login')
  const [searchParams] = useSearchParams()

  const userId = searchParams.get('user') ?? ''
  const token = searchParams.get('token') ?? ''

  // Tracked separately from the mutation's own isPending: the generated hook shares a
  // static mutationKey across every usage, so its status can flicker under React
  // StrictMode's double-invoke. Our own state only ever moves pending -> success/error.
  const [state, setState] = useState<ConfirmationState>('pending')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { mutate: confirmEmail } = usePostApiUsersUserIdConfirmationToken({
    mutation: {
      onSuccess: () => setState('success'),
      onError: (error) => {
        setErrorMessage(isServerUnreachable(error) ? t('serverUnreachable') : t('emailConfirmationPage.errors.linkInvalid'))
        setState('error')
      },
    },
  })

  // Confirmation is a side effect of opening the link, not a user action — trigger it
  // once on mount. The ref guards against React StrictMode's double-invoke in dev.
  const triggered = useRef(false)
  useEffect(() => {
    if (triggered.current) return
    triggered.current = true

    if (!userId || !token) {
      setErrorMessage(t('emailConfirmationPage.errors.linkInvalid'))
      setState('error')
      return
    }
    confirmEmail({ userId, token })
  }, [userId, token, confirmEmail, t])

  return (
    <WallpaperBackground>
      <Card className="w-full max-w-sm">
        <CardHeader className="flex flex-row items-start justify-between">
          <div>
            <CardTitle className="text-2xl">{t('emailConfirmationPage.title')}</CardTitle>
            <CardDescription>
              {state === 'success'
                ? t('emailConfirmationPage.descriptionSuccess')
                : state === 'error'
                  ? t('emailConfirmationPage.descriptionError')
                  : t('emailConfirmationPage.descriptionPending')}
            </CardDescription>
          </div>
          <div className="flex items-center gap-1">
            <ThemeToggle />
            <LanguageSwitcher />
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {errorMessage && <p className="text-sm text-destructive">{errorMessage}</p>}
            {state !== 'pending' && (
              <Link to="/login">
                <Button className="w-full">{t('emailConfirmationPage.goToLogin')}</Button>
              </Link>
            )}
          </div>
        </CardContent>
      </Card>
    </WallpaperBackground>
  )
}
